/**
 * WizardLayout Component
 * Renders wizard layout with steps and prev/next navigation
 */

import React, { ReactNode } from 'react';
import './WizardLayout.css';
import { FormContainerConfig } from '../types/index';
import { ValidationBadge } from './ValidationBadge';

export interface WizardLayoutProps {
  config: FormContainerConfig;
  activeIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  canNext: boolean;
  errors: Record<string, any[]>;
  children: ReactNode;
  theme?: 'dark' | 'light';
}

/**
 * Wizard layout component
 * Step indicator at top, navigation buttons at bottom
 */
export const WizardLayout: React.FC<WizardLayoutProps> = ({
  config,
  activeIndex,
  onPrev,
  onNext,
  onSubmit,
  canNext,
  errors,
  children,
  theme = 'light',
}) => {
  const totalSteps = config.childForms.length;
  const isFirstStep = activeIndex === 0;
  const isLastStep = activeIndex === totalSteps - 1;

  return (
    <div className="af-wizard-layout">
      {/* Step indicators */}
      {config.showProgressIndicator !== false && (
        <div className="af-wizard-layout__steps">
          {config.childForms.map((child, index) => (
            <React.Fragment key={child.formID}>
              <div
                className={`af-wizard-layout__step-indicator${index <= activeIndex ? ' af-wizard-layout__step-indicator--completed' : ''}`}
              >
                {index + 1}
              </div>
              {index < config.childForms.length - 1 && (
                <div className={`af-wizard-layout__step-connector${index < activeIndex ? ' af-wizard-layout__step-connector--completed' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="af-wizard-layout__content">
        {children}
      </div>

      {/* Navigation buttons — hidden entirely when all buttons are suppressed (e.g. view mode) */}
      {(config.showPreviousButton !== false || config.showNextButton !== false || config.showSubmitButton !== false) && (
        <div className="af-wizard-layout__nav">
          {config.showPreviousButton !== false && (
            <button
              type="button"
              className="af-wizard-layout__btn af-wizard-layout__btn--prev"
              onClick={onPrev}
              disabled={isFirstStep}
            >
              ← Previous
            </button>
          )}

          <div className="af-wizard-layout__nav-right">
            {config.allowSkipSteps && !isLastStep && config.showNextButton !== false && (
              <button
                type="button"
                className="af-wizard-layout__btn af-wizard-layout__btn--skip"
                onClick={onNext}
              >
                Skip
              </button>
            )}

            {!isLastStep && config.showNextButton !== false && (
              <button
                type="button"
                className="af-wizard-layout__btn af-wizard-layout__btn--next"
                onClick={onNext}
                disabled={!canNext}
              >
                Next →
              </button>
            )}

            {isLastStep && config.showSubmitButton !== false && (
              <button
                type="submit"
                className="af-wizard-layout__btn af-wizard-layout__btn--submit"
                onClick={onSubmit}
              >
                Submit
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

WizardLayout.displayName = 'WizardLayout';
